import React, { createContext, ReactNode, useContext, useState } from "react";
import { PropostaData } from "../types/proposta-data-interface";
import { RequestAcionamento } from "../types/resgitro-acionamento-interface";
import { useMutation } from "@tanstack/react-query";
import { apiModulo, apiWS } from "../services/api";
import { useModal } from "../hooks/ModalContext";

interface PropostasContextProviderProps {
  children: ReactNode;
}

interface RequestGetProposta {
  url: string;
  cpf_cnpj: string;
  codcredor: string;
  contrato?: string;
  vencimento?: string;
  formapagamento?: string
  usuario?: string
}

interface Vencimentos {
  vencimento: string;
  datamaxvencimento: string;
}

export interface CanalEnvio {
  canalrecebimento: string;
  destinorecebimento: string;
}

export interface PropostaSelected extends PropostaData {
  contrato: string;
  canalrecebimento?: string;
  destinorecebimento?: string;
}

export interface ChannelsChecked {
  isChecked: boolean;
  value: string;
}

interface PropostasContextProps {
  getPropostas({
    codcredor,
    contrato,
    cpf_cnpj,
    url,
    vencimento,
  }: RequestGetProposta): Promise<void>;
  getSelectedProposta(proposta?: PropostaSelected): void;
  getCanalEnvio(canalEnvio: CanalEnvio): void;
  getSelectedContrato(contrato?: string): void;
  sendAcionamento(data: RequestAcionamento): Promise<void>;
  sendAcionamentoGazin(data: RequestAcionamento): Promise<void>;
  setEmailChecked: React.Dispatch<React.SetStateAction<ChannelsChecked>>;
  setSMSChecked: React.Dispatch<React.SetStateAction<ChannelsChecked>>;
  setFormaPagamnento: React.Dispatch<React.SetStateAction<string>>;
  clearPropostas(): void;
  getDateVencimento(date: string): void;
  propostasData: PropostaData[];
  vencimentos: Vencimentos;
  selectedProposta: PropostaSelected;
  canalEnvio: CanalEnvio;
  selectedContrato: string | undefined;
  date: string;
  emailChecked: ChannelsChecked;
  smsChecked: ChannelsChecked;
  formaPagamento: string;
}

const PropostasContext = createContext<PropostasContextProps>(
  {} as PropostasContextProps
);

export const PropostasProvider = ({
  children,
}: PropostasContextProviderProps) => {
  const [propostasData, setPropostasData] = useState<PropostaData[]>([]);
  const [vencimentos, setVencimentos] = useState<Vencimentos>(
    {} as Vencimentos
  );
  const [selectedProposta, setSelectedProposta] = useState<PropostaSelected>(
    {} as PropostaSelected
  );
  const [canalEnvio, setCanalEnvio] = useState<CanalEnvio>({} as CanalEnvio);
  const [selectedContrato, setSelectedContrato] = useState<string>();
  const [date, setDate] = useState(
    vencimentos?.datamaxvencimento ? vencimentos.datamaxvencimento : ""
  );
  const [emailChecked, setEmailChecked] = useState<ChannelsChecked>(
    {} as ChannelsChecked
  );
  const [smsChecked, setSMSChecked] = useState<ChannelsChecked>(
    {} as ChannelsChecked
  );
  const [formaPagamento, setFormaPagamnento] = useState("Boleto");
  const { setModal } = useModal()

  const mutationProposta = useMutation({
    mutationFn: ({
      codcredor,
      contrato,
      cpf_cnpj,
      url,
      vencimento,
      formapagamento,
      usuario
    }: RequestGetProposta) =>
      apiModulo.post("/proposals", {
        codcredor,
        contrato,
        cpf_cnpj,
        url,
        app: 'ACIONA_WEB',
        vencimento,
        formapagamento,
        usuario
      }),
    mutationKey: ["form"],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true)
  });

  const mutationAcionamento = useMutation({
    mutationFn: ({ app, credor, cpfCnpj, idacionamento, observacao, stage, telefone, tipo, usuario, agendamento, telefoneagendamento, empresa,
      url,
      contrato }: RequestAcionamento) => apiModulo.post('/negotiation/register', {
        codcredor: credor, cpf_cnpj: cpfCnpj, idacionamento, observacao, telefone, tipo, usuario, stage, agendamento, telefoneagendamento, empresa,
        url,
        contrato, app: app ? app : 'ACIONA_WEB'
      }), mutationKey: ['form'],
    onError: () => setModal('Ops! Ocorreu um erro em nosso sistema. Tente novamente mais tarde!', 'Erro', false, true)
  })

  const mutationAcionamentoGazin = useMutation({
    mutationFn: ({
      app,
      credor,
      cpfCnpj,
      idacionamento,
      observacao,
      stage,
      url,
      telefone,
      tipo,
      usuario,
      agendamento,
      telefoneagendamento,
      empresa,
    }: RequestAcionamento) =>
      apiWS.post("/acionamento", {
        app,
        credor,
        cpfCnpj,
        idacionamento,
        observacao,
        telefone,
        tipo,
        usuario,
        stage,
        url,
        agendamento,
        telefoneagendamento,
        empresa,
      }),
    mutationKey: ["form"],
    onError: (error) => console.log(error),
  });

  async function getPropostas({
    codcredor,
    contrato,
    cpf_cnpj,
    url,
    vencimento,
    formapagamento,
    usuario
  }: RequestGetProposta) {
    const { data } = await mutationProposta.mutateAsync({
      codcredor,
      contrato,
      cpf_cnpj,
      url,
      vencimento,
      formapagamento,
      usuario
    });
    setVencimentos({
      vencimento: data.message.vencimento,
      datamaxvencimento: data.message.datamaxvencimento,
    });
    setPropostasData(data.message.propostas);
  }

  function getDateVencimento(date: string) {
    setDate(date);
  }

  function clearPropostas() {
    setVencimentos({} as Vencimentos);
    setPropostasData([]);
    setCanalEnvio({} as CanalEnvio);
    setSelectedProposta({} as PropostaSelected);
  }

  function getSelectedProposta(proposta?: PropostaSelected) {
    if (proposta) {
      setSelectedProposta(proposta);
    } else {
      setSelectedProposta({} as PropostaSelected);
    }
  }

  function getCanalEnvio(canalEnvio: CanalEnvio) {
    setCanalEnvio(canalEnvio);

    setSelectedProposta((prevState) => ({
      ...prevState,
      canalrecebimento: canalEnvio.canalrecebimento,
      destinorecebimento: canalEnvio.destinorecebimento,
    }));
  }

  function getSelectedContrato(contrato?: string) {
    if (contrato) {
      setSelectedContrato(contrato);
    } else {
      setSelectedContrato("");
    }
  }

  async function sendAcionamento(data: RequestAcionamento) {
    await mutationAcionamento.mutateAsync(data);
  }
  async function sendAcionamentoGazin(data: RequestAcionamento) {
    console.log(data)
    await mutationAcionamentoGazin.mutateAsync(data);
  }

  return (
    <PropostasContext.Provider
      value={{
        getPropostas,
        propostasData,
        vencimentos,
        getSelectedProposta,
        selectedProposta,
        getCanalEnvio,
        canalEnvio,
        getSelectedContrato,
        selectedContrato,
        sendAcionamento,
        sendAcionamentoGazin,
        clearPropostas,
        date,
        getDateVencimento,
        smsChecked,
        setSMSChecked,
        emailChecked,
        setEmailChecked,
        formaPagamento,
        setFormaPagamnento,
      }}
    >
      {children}
    </PropostasContext.Provider>
  );
};

export const usePropostas = () => useContext(PropostasContext);
